import React, { useState, useEffect, useMemo, useContext } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  editPodcast,
  fetchDraftPodcast,
  fetchPodcastTranscription,
  invalidateGenerateTranscriptionPodcastState,
  invalidateQuickGeneratePodcastState,
  invalidatePublishPodcast,
  publishPodcast,
  quickGeneratePodcast,
  syncPodcastProcessingState,
  updatePodcast,
  updatePodcastProperty,
  updateValidationErrorsForPodcast,
  validatePodcast,
  invalidateDraftPodcast,
} from "src/redux/actions/coaching/podcast";

import { Podcast, PodcastValidationError } from "src/types/coaching/podcast";
import { PodcastMenuBar } from "../components/menu";
import { ScrollableContainer } from "src/components/scroller";

import { useTransition, animated, useSpring } from "@react-spring/web";
import TextareaAutosize from "react-autosize-textarea";
import Reveal from "src/components/animation";
import { CourseLearners } from "src/pages/coaching/manage/courses/edit/components/learners";
import { AddLearnersDialog } from "src/pages/coaching/manage/courses/edit/components/learners/add";
import { SelectSkillsDialog } from "src/pages/coaching/manage/courses/edit/components/selectSkills";
import Thumbnail from "src/components/thumbnail";
import Button from "src/components/button";
import { hasInsufficientContentError, preparePodcastRequest, validatePodcastRequest } from "./helper";
import { AddAssetsDialog } from "src/components/asset/add";

import userRoleService from "src/utils/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import { PodcastNotFound } from "src/pages/coaching/components/notFound";
import { EditInStudio } from "../components/studio";
import { BannerView } from "src/components/banner";
import { Banner } from "src/types/coaching/courses";
import { EditCourseThumbnail } from "src/pages/coaching/manage/courses/edit/thumbnail";
import { fetchCourseLearners } from "src/redux/actions/coaching/learners";

import _ from "lodash";
import cx from "classnames";

import styles from "./edit.module.scss";
import { LoadingPodcastPage } from "src/pages/coaching/components/podcast";
import { FileService } from "src/utils/file.service";
import { PodcastPlayer } from "src/pages/coaching/components/podcast/components/player";
import Tooltip from "src/components/tooltip";
import { toast } from "react-toastify";
import { PodcastTranscriptRowPlaceholder, PodcastTranscriptsContainer } from "src/pages/coaching/components/podcast/components/transcripts";
import { SoundVisualizer } from "src/pages/coaching/components/simulation/view/simulator/visualiser";
import { PodcastContext, PodcastContextProps } from "./context";
import { DurationSlider, PodcastTypeField } from "../components/configurations";
import { DURATION_CHOICES } from "src/constants/simulation";
import pluralize from "pluralize";
import { TabBar } from "src/components/tabbar";
import { SaveLearningsDialog } from "../../courses/edit/components/menu";

export function EditPodcastPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const params = useParams();
  const id = params.id as string;

  const [contextState, setContextState] = useState<PodcastContextProps>({});
  const [showSaveChangesDialog, setShowSaveChangesDialog] = useState(false);

  const persistedPodcast: Podcast = useSelector(
    (state: any) =>
      _.get(state.coaching.podcasts, `podcast[${id}].item`) as Podcast,
  );

  const loading = useSelector((state: any) => {
    return _.get(state, `coaching.podcasts.podcast[${id}].loading`);
  });

  const loaded = useSelector((state: any) => {
    return _.get(state, `coaching.podcasts.podcast[${id}].loaded`, false);
  });

  const podcast: Podcast = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.item`, null) as Podcast;
  });

  const podcastErrorCode = useSelector((state: any) => {
    return _.get(
      state,
      `coaching.podcasts.podcast[${id}].error.errors[0].code`,
    );
  });
  
  const podcastUpdated = !_.isEqual(_.omit(podcast,'transcription','lastPublished','hasChanges','status'),
  _.omit(persistedPodcast,'transcription','lastPublished','hasChanges','status'));

  const transcriptUrl = useMemo(() => {
    return _.get(podcast, "transcriptUrl", null);
  }, [podcast]);

  const emptyPodcast = !transcriptUrl;
  const hasTopics = _.size(podcast?.topics) > 0;

  const { publishing, published } = useSelector((state: any) => {
    return {
      publishing: _.get(state.coaching.podcasts, 'publish.publishing'),
      published: _.get(state.coaching.podcasts, 'publish.published')
    }
  });

  const [isPublished, setIsPublished] = useState(false);

  const [menuState, setMenuState] = useState({
    primaryAction: null,
    secondaryAction: null,
  });

  const quickGeneratingPodcast = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.quickGenerate.generating`, false);
  });

  const transcriptGenerated = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.generateTranscript.generated`, null);
  });

  const audioGenerated = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.generateAudio.generated`, null);
  });

  const editInStudio = useMemo(() => {
    return menuState.secondaryAction === "edit-studio";
  }, [menuState]);

  const shouldGenerateTranscript = useMemo(() => {
    const needsProcessing = _.get(podcast, "transcriptProcessing.status") === 'pending';
    if (needsProcessing) {
      return true;
    }

    return false;
  }, [podcast]);

  const shouldGenerateAudio = useMemo(() => {
    if (shouldGenerateTranscript) {
      return true;
    }

    const needsProcessing = _.get(podcast, "audioProcessing.status") === 'pending';
    if (needsProcessing) {
      return true;
    }

    return false;
  }, [podcast, shouldGenerateTranscript]);

  const processingTranscripts = useSelector((state: any) => {
    const generating = _.get(state.coaching.podcasts, `edit.generateTranscript.generating`, false);
    const status = _.get(state, `coaching.podcasts.edit.item.transcriptProcessing.status`, null);
    return generating || _.includes(["in_progress", "requested"], status);
  });

  const processingAudio = useSelector((state: any) => {
    const generating = _.get(state.coaching.podcasts, `edit.generateAudio.generating`, false);
    const status = _.get(state, `coaching.podcasts.edit.item.audioProcessing.status`, null);
    return generating || _.includes(["in_progress", "requested"], status);
  });

  const editTransitions = useTransition(!isPublished, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  const editStudioTransition = useTransition(editInStudio, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 1 },
  });

  const emptyTransitions = useTransition(emptyPodcast, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  const detailsTransitions = useTransition(processingTranscripts || hasTopics || !emptyPodcast, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  const handleGoToError = (error?: PodcastValidationError) => {
    setContextState((prev) => {
      return {
        ...prev,
        validationError: error,
      };
    });
  };

  const handleShowValidationErrorDialog = (show: boolean) => {
    setContextState((prev) => {
      return {
        ...prev,
        showValidationErrorDialog: show,
      };
    });
  };

  const handleMenuStateChange = (state) => {
    setMenuState(state);
  };

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(
      updatePodcastProperty({
        podcast: podcast.id!,
        property,
        value,
      }),
    );
  };

  const handleSave = () => {
    const validationErrors = validatePodcastRequest(podcast);
    if (_.size(validationErrors) > 0) {
      dispatch(
        updateValidationErrorsForPodcast({
          podcast: podcast.id!,
          errors: validationErrors,
        }),
      );
    }

    const request = preparePodcastRequest(persistedPodcast, podcast);

    dispatch(
      updatePodcast({
        id: podcast.id!,
        request: request,
      }),
    );
    
  };

  const handlePublish = () => {
    const validationErrors = validatePodcastRequest(podcast);

    if (_.size(validationErrors) > 0) {
      dispatch(
        updateValidationErrorsForPodcast({
          podcast: podcast.id!,
          errors: validationErrors,
        }),
      );

      return;
    }

    dispatch(publishPodcast(podcast.id!));
  };

  const onBackClick = () => {
    if (processingTranscripts || processingAudio) {
      navigate("/coaching/manage");
    }

    if (!podcastUpdated) {
      navigate("/coaching/manage");
    }

    setShowSaveChangesDialog(true);
  };


  useEffect(() => {
    if (id) {
      dispatch(fetchDraftPodcast(id));

      const podcastLearnerRequest = {
        course: id,
        status: "ALL",
        page: 0,
        limit: 10,
      };
      dispatch(fetchCourseLearners(podcastLearnerRequest));

      setContextState((prev) => {
        return {
          ...prev,
          id: id,
          gotoError: handleGoToError,
          setShowValidationErrorDialog: handleShowValidationErrorDialog,
        };
      });
    }

    return () => {
      dispatch(invalidateDraftPodcast(id));
    };
  }, [id]);

  useEffect(() => {
    if (persistedPodcast && persistedPodcast.id !== podcast?.id) {
      dispatch(editPodcast(persistedPodcast));
    }
  }, [persistedPodcast, podcast]);

  useEffect(() => {
    if (podcast) {
      dispatch(validatePodcast(podcast.id!));
    }
  }, [podcast]);

  useEffect(() => {
    if (menuState.primaryAction === "save") {
      handleSave();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        };
      });
    } else if (menuState.primaryAction === "publish") {
      handlePublish();
      setMenuState((prev) => {
        return {
          ...prev,
          primaryAction: null,
        };
      });
    }
  }, [menuState]);

  useEffect(() => {
    if ((transcriptGenerated || audioGenerated) && podcast.id) {
      dispatch(syncPodcastProcessingState(podcast.id!));
    }

    if (transcriptGenerated && podcast.id) {
      dispatch(invalidateGenerateTranscriptionPodcastState(podcast.id!));
    }

    if (quickGeneratingPodcast && audioGenerated && podcast.id) {
      dispatch(invalidateQuickGeneratePodcastState(podcast.id!));
      dispatch(fetchPodcastTranscription(podcast.id!));
    }
  }, [transcriptGenerated, audioGenerated, quickGeneratingPodcast]);

  useEffect(() => {
    if (published) {
      setIsPublished(true);
    }
  }, [published]);

  useEffect(() => {
    if (transcriptUrl) {
      dispatch(fetchPodcastTranscription(podcast.id!));
    }
  }, [transcriptUrl]);

  if (!userRoleService.canCreateCourse()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if (podcastErrorCode === 10007) {
    return (
      <PodcastNotFound
        onBack={() => {
          navigate(`/coaching/manage`);
        }}
      />
    );
  }

  if ((loading && podcast?.id != id) || !podcast) {
    return (
      <LoadingPodcastPage onBack={() => {
        navigate(`/coaching/manage`);
      }} />
    );
  }

  return (
    <PodcastContext.Provider value={contextState}>
      <div key={id} className={styles.podcastContainer}>
        <div className={styles.header}>
          <div className={styles.backContainer}>
            <div
              className={styles.back}
              onClick={onBackClick}
            >
              <i className="paperflite-left-arrow"></i>
              <span>back to <q>/studio</q></span>
            </div>
          </div>
          <div className={styles.menubar}>
            <PodcastMenuBar
              podcast={podcast}
              menuState={menuState}
              onMenuStateChange={handleMenuStateChange}
            />
          </div>
        </div>

        {editTransitions(
          (transitionStyles, item) =>
            item && (
              <animated.div className={styles.editContainer} style={transitionStyles}>
                <div className={styles.top}>
                  <div className={styles.left}>
                    <PodcastHeader podcast={podcast} />

                    <div className={styles.detailsContainer}>
                      {emptyTransitions((style, item) =>
                        item && (
                          <animated.div className={styles.stack} style={style}>
                            <UploadAssetContainer podcast={podcast} />
                          </animated.div>
                        )
                      )}

                      {detailsTransitions((style, item) =>
                        item && (
                          <animated.div className={styles.stack} style={style}>
                            <PodcastDetailContainer podcast={podcast} onChange={handlePropertyChange} />
                          </animated.div>
                        )
                      )}
                    </div>
                  </div>

                  <RightPanel podcast={podcast} />

                </div>
                {!shouldGenerateAudio && !processingAudio && podcast?.audio?.url && (
                  <div className={styles.bottom}>
                    <PodcastPlayer
                      podcast={podcast}
                      initialTimeSpent={0}
                      autoPlay={false}
                      onPlay={() => { }}
                      onAnalyticsChange={() => { }}
                    />
                  </div>
                )}

                {processingAudio && (
                  <div className={styles.bottom}>
                    <div className={styles.audioProcessing}>
                      <SoundVisualizer
                        disabled={false}
                        volumeLevel={1}
                        barCount={120}
                        barWidth="2px"
                        height={30}
                        color="#FF45C2"
                        durationInSeconds={0.25}
                        cornerRadius={8}
                        verticalAlignment="center"
                      />
                      <div className={styles.message}>Generating audio...</div>
                    </div>
                  </div>
                )}
              </animated.div>
            ),
        )}
      </div>

      {editStudioTransition((style, item) => {
        return item ? (
          <animated.div style={style} className={cx(styles.editInStudioContainer)}>
            <EditInStudio
              podcast={podcast}
              menuState={menuState}
              onMenuStateChange={handleMenuStateChange}
            />
          </animated.div>
        ) : false;
      })}
      
      {showSaveChangesDialog && !processingTranscripts && !processingAudio && (
        <SaveLearningsDialog
          show={showSaveChangesDialog}
          course={podcast}
          onFinish={() => setShowSaveChangesDialog(false)}
          onClose={() => setShowSaveChangesDialog(false)}
          handleSave={handleSave}
          type="podcast"
        />
      )}

      <PublishView isOpen={isPublished} podcast={podcast} onClose={() => setIsPublished(false)} />
    </PodcastContext.Provider>
  );
}

interface PublishViewProps {
  isOpen: boolean;
  podcast: Podcast;
  onClose: () => void;
}

export const PublishView: React.FC<PublishViewProps> = ({
  isOpen,
  podcast,
  onClose
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [closePublish, setClosePublish] = useState(false);

  const publishTransitions = useTransition(isOpen && !closePublish, {
    initial: { transform: "translateY(0%)" },
    from: { transform: "translateY(100%)" },
    enter: { transform: "translateY(0%)" },
    leave: { transform: "translateY(100%)" },
    config: { duration: 300 },
    delay: 100,
    onRest: () => {
      if (closePublish) {
        setClosePublish(false);
        onClose();
      }
    },
  });

  useEffect(() => {
    return () => {
      dispatch(invalidatePublishPodcast());
    };
  }, []);

  return publishTransitions(
    (transitionStyles, item) =>
      item && (
        <animated.div
          className={styles.publishContainer}
          style={transitionStyles}
        >
          <div className={styles.left}>
            <Thumbnail className={styles.thumbnail} media={podcast.banner?.background} position={podcast.banner?.position} crop={podcast.banner?.crop} width={200} defaultImage="/images/assets/podcastThumbnail.jpg" />
            <img className={styles.title} src="/images/coaching/congratulations.svg" alt="Congratulations" />
            <p className={styles.message}>
              you’ve successfully published the podcast “{podcast.name || 'Untitled Podcast'}”.
            </p>
            <div className={styles.actions}>
              <Button
                className={cx(styles.action, styles.primary)}
                variant="primary"
                icon="paperflite-right-arrow"
                text="view podcast details"
                onClick={() => setClosePublish(true)}
              />
              <Button
                className={styles.action}
                variant="secondary"
                icon="paperflite-close"
                text="exit"
                onClick={() => navigate(`/coaching/manage`)}
              />
            </div>
          </div>
          <div className={styles.right}>
            <img className={styles.illustration} src="/images/coaching/confetti.gif" />
          </div>
        </animated.div>
      ),
  );
};

interface UploadAssetContainerProps {
  podcast: Podcast;
}

const UploadAssetContainer: React.FC<UploadAssetContainerProps> = ({ podcast }) => {
  const dispatch = useDispatch();
  const [showAddAssets, setShowAddAssets] = useState(false);
  const [assets, setAssets] = useState<any[]>([]);
  const [duration, setDuration] = useState<number>(0);
  const [podcastType, setPodcastType] = useState<string>("conversational");

  const persistedPodcast: Podcast = useSelector(
    (state: any) =>
      _.get(state.coaching.podcasts, `podcast[${podcast.id}].item`) as Podcast,
  );

  const quickGenerating = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.quickGenerate.generating`, false);
  });

  const processingTranscript = useSelector((state: any) => {
    const generating = _.get(state.coaching.podcasts, `edit.generateTranscript.generating`, false);
    const status = _.get(state, `coaching.podcasts.edit.item.transcriptProcessing.status`, null);
    return generating || _.includes(["in_progress", "requested"], status);
  });

  const processingAudio = useSelector((state: any) => {
    const generating = _.get(state.coaching.podcasts, `edit.generateAudio.generating`, false);
    const status = _.get(state, `coaching.podcasts.edit.item.audioProcessing.status`, null);
    return generating || _.includes(["in_progress", "requested"], status);
  });

  const empty = _.size(assets) === 0;
  const generating = quickGenerating || processingTranscript || processingAudio;
  const hasTopics = _.size(podcast?.topics) > 0;
  const podcastError = _.get(podcast, "transcriptProcessing.error", null);

  const emptyTransition = useTransition(empty && !generating, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  const editingTransition = useTransition(!empty && !generating, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  const progressTransition = useTransition(generating, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  const handleAddAsset = () => {
    setShowAddAssets(true);
  }

  const handleFinishAddAssets = (assets: any[]) => {
    const invalidAssets = assets.filter((asset) => {
      const fileType = FileService.getFileType(asset.metadata);
      const allowedTypes = ["PDF", "PPT", "PPTX", "DOC", "DOCX", "GOOGLE_SLIDES", "LINK"];
      return !_.includes(allowedTypes, fileType);
    });

    if (_.size(invalidAssets) > 0) {
      toast.error("Only pptx, pdf assets can be added to podcasts");
      return false;
    }

    const filteredAssets = assets.filter((asset) => {
      const fileType = FileService.getFileType(asset.metadata);
      const allowedTypes = ["PDF", "PPT", "PPTX", "DOC", "DOCX", "GOOGLE_SLIDES", "LINK"];
      return _.includes(allowedTypes, fileType);
    });

    if (_.size(filteredAssets) === 0) {
      toast.error("No valid assets selected. Please select pptx, pdf assets.");
      return false;
    }

    if (_.size(filteredAssets) > 5) {
      toast.error("You can only add a maximum of 5 assets to a podcast.");
      return false;
    }

    if (filteredAssets.length > 0) {
      setAssets(filteredAssets);
      handleCloseAddAssets();
    }

    return true;
  }

  const handleCloseAddAssets = () => {
    setShowAddAssets(false);
  }

  const entityAction = (entity: any) => {
    if (entity.entityType === "asset") {
      const fileType = FileService.getFileType(entity.metadata);
      const allowedTypes = ["PDF", "PPT", "PPTX", "DOC", "DOCX", "GOOGLE_SLIDES", "LINK"];
      const disabled =
        fileType && !_.includes(allowedTypes, fileType);
      return (
        <Tooltip
          content="Only pptx, pdf assets can be added to podcasts"
          placement="auto"
          disabled={!disabled}
        >
          <button className={styles.action}>
            <i
              className={cx(
                styles.icon,
                disabled ? "paperflite-lock" : "paperflite-add",
              )}
            ></i>
          </button>
        </Tooltip>
      );
    }
    return false;
  };

  const assetSelectionFilter = (entity: any) => {
    if (entity.entityType === "asset") {
      const fileType = FileService.getFileType(entity.metadata);
      const allowedTypes = ["PDF", "PPT", "PPTX", "DOC", "DOCX", "GOOGLE_SLIDES", "LINK"];
      return _.includes(allowedTypes, fileType);
    }
    return false;
  }

  const handleGeneratePodcast = () => {
    if (_.size(assets) === 0) {
      toast.error("Please add assets to generate podcast.");
      return;
    }

    if (!podcastType) {
      toast.error("Please select a valid podcast type.");
      return;
    }

    const updatedPodcast = {
      ...podcast,
      assets: assets,
      duration: duration,
      type: podcastType
    };

    const podcastRequest = preparePodcastRequest(persistedPodcast, updatedPodcast);
    console.log("Quick generating podcast with request: ", podcastRequest);

    dispatch(
      quickGeneratePodcast({
        id: podcast!.id!,
        request: podcastRequest,
        generateAudio: true,
      }),
    );
  }

  useEffect(() => {
    const assets = _.get(podcast, "assets", []);
    setAssets(assets);
  }, [podcast.assets]);

  return (
    <div className={styles.quickGenerateContainer}>
      {emptyTransition(
        (style, item) =>
          item && (
            <animated.div className={cx(styles.addAssetsContainer, styles.stack)} style={style}>
              <h1 className={styles.title}>You need to add assets to kick off your podcast journey!</h1>
              <p className={styles.description}>After uploading the source, you&apos;ll have the option to select the type of podcast & time.</p>
              <div className={styles.actions}>
                <Button
                  variant="primary"
                  className={styles.addAssetsButton}
                  text="add assets"
                  icon="paperflite-add"
                  onClick={handleAddAsset}
                />
              </div>
            </animated.div>
          )
      )}

      {editingTransition(
        (style, item) =>
          item && (
            <animated.div className={cx(styles.editConfigurationContainer, styles.stack)} style={style}>
              <div className={styles.assets}>
                {(assets || []).map((asset, index) => {
                  const hasError = hasInsufficientContentError(podcast, asset.id);

                  return (
                    <div key={index} className={styles.asset}>
                      <Thumbnail
                        className={styles.thumbnail}
                        media={asset.icon}
                        defaultImage="/images/assets/default_asset.jpg"
                      />
                      {hasError && (
                        <div className={styles.error}>
                          <Tooltip content="This asset has insufficient content" placement="top">
                            <i className={cx(styles.icon, "paperflite-danger")}></i>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              {!podcastError &&
                <>
                  <div className={cx(styles.field, styles.duration)}>
                    <span className={styles.message}>{_.size(assets)} {pluralize('asset', _.size(assets))} has been added, now choose the max duration
                      <Tooltip
                        content={
                          <div style={{ textAlign: 'left', alignItems: 'flex-start' }}>
                            <b>Note:</b> Podcast duration is automatically optimized for uploaded content. The max duration sets an upper limit, not an exact length, and may result in stretched or shortened coverage
                          </div>
                        }
                        placement="top"
                      >
                        <i className={cx(styles.icon, 'paperflite-danger')}></i>
                      </Tooltip>
                    </span>
                    <div className={styles.input}>
                      <DurationSlider
                        value={duration}
                        options={DURATION_CHOICES}
                        onChange={(value) => setDuration(value)}
                      />
                    </div>
                  </div>
                  <div className={cx(styles.field, styles.podcastType)}>
                    <span>and select the type of podcast you wanna listen</span>
                    <div className={styles.input}>
                      <PodcastTypeField
                        value={podcastType}
                        onChange={(value) => setPodcastType(value)}
                      />
                    </div>
                  </div>
                  <div className={styles.actions}>
                    <Button
                      variant="primary"
                      className={styles.generateButton}
                      text="generate podcast"
                      icon="paperflite-right-arrow"
                      onClick={handleGeneratePodcast}
                    />
                  </div>
                </>
              }

              {podcastError && (
                <div className={styles.errorContainer}>
                  <h1 className={styles.title}>transcript generation failed</h1>
                  <p className={styles.description}>
                    {podcastError.code === 'podcast.generation.insufficient_content' &&
                      'this usually occurs when the asset doesn\'t have enough content in them...'
                    }
                    {podcastError.code === 'podcast.generation.transcripts' &&
                      'something went wrong while generating the transcript...'
                    }
                  </p>
                  <div className={styles.actions}>
                    <Button
                      variant="primary"
                      className={styles.generateButton}
                      text="retry"
                      icon="paperflite-reverse"
                      onClick={handleGeneratePodcast}
                    />

                    {podcastError.code === 'podcast.generation.insufficient_content' &&
                      <Button
                        variant="secondary"
                        className={styles.uploadButton}
                        text="upload new assets"
                        icon="paperflite-upload"
                        onClick={handleAddAsset}
                      />
                    }
                  </div>
                </div>
              )}
            </animated.div>
          )
      )}

      {progressTransition(
        (style, item) =>
          item && (
            <animated.div className={cx(styles.processingContainer, styles.stack)} style={style}>
              {!hasTopics &&
                <>
                  <h1 className={styles.title}>generating <u>topics...</u></h1>
                  <p className={styles.description}>This may take a few minutes. Feel free to go back—we&apos;ll notify you once it&apos;s ready.</p>
                </>
              }

              {hasTopics &&
                <>
                  <h1 className={styles.title}>generating <u>transcript...</u></h1>
                  <p className={styles.description}>This may take a few minutes. Feel free to go back—we&apos;ll notify you once it&apos;s ready.</p>
                </>
              }
            </animated.div>
          )
      )}

      <AddAssetsDialog
        show={showAddAssets}
        selectableEntityTypes={["asset"]}
        allowInternalEntities={true}
        source={{
          type: "course",
          entity: podcast as any,
        }}
        allowBackgroundUpload={false}
        initialEntities={assets}
        entityAction={entityAction}
        selectionFilter={assetSelectionFilter}
        onFinish={handleFinishAddAssets}
        onClose={handleCloseAddAssets}
      />
    </div>
  );
}

interface PodcastHeaderProps {
  podcast: Podcast;
}

const PodcastHeader: React.FC<PodcastHeaderProps> = ({ podcast }) => {
  const nameInputRef = React.useRef<any>(null);

  const dispatch = useDispatch();

  const name = useMemo(() => {
    return _.get(podcast, "name") || "";
  }, [podcast]);

  const nameError = useSelector((state: any) => {
    const errors = _.get(state.coaching.podcasts, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "name" });
  });

  const thumbnailError = useSelector((state: any) => {
    const errors = _.get(state.coaching.podcasts, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "banner" });
  });

  const banner = useMemo(() => {
    return _.get(podcast, "banner");
  }, [podcast]);

  const bannerError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "banner" });
  });

  const [editThumbnail, setEditThumbnail] = useState(false);

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(
      updatePodcastProperty({
        podcast: podcast.id!,
        property,
        value,
      }),
    );
  };

  const handleNameChange = (e: any) => {
    let input = e.target.value;
    input = input.replace(/\n/g, "");
    handlePropertyChange("name", input);
  };

  const handleThumbnailChange = (banner: Banner) => {
    handlePropertyChange("banner", banner);
    setEditThumbnail(false);
  };

  const podcastDuration = useMemo(() => {
    const duration = _.get(podcast, "audio.duration") || 0.0;
    if (duration > 0) {
      return FileService.formatDuration(duration, 'long')
    }

    return null;
  }, [podcast]);

  const podcastType = _.get(podcast, "type");

  return (
    <div className={styles.details}>
      <div className={styles.nameRow}>
        <div className={cx(styles.field, styles.thumbnail)}>
          <div className={styles.inner}>
            <div className={styles.overlay}>
              <i className="paperflite-edit"></i>
            </div>
            <div
              className={cx(styles.field, styles.thumbnail)}
              onClick={() => {
                setEditThumbnail(true);
              }}
            >
              <div className={styles.inner}>
                <div className={styles.overlay}>
                  <i className="paperflite-edit"></i>
                </div>
                <BannerView
                  banner={banner as any}
                  defaultBanner="/images/assets/podcastThumbnail.jpg"
                  overlay={false}
                />
              </div>
              <Reveal show={bannerError != null}>
                <div className={styles.error}>
                  <i className="fa-solid fa-triangle-exclamation"></i>
                  <span>{bannerError?.message}</span>
                </div>
              </Reveal>
            </div>
          </div>
          <Reveal show={thumbnailError != null}>
            <div className={styles.error}>
              <i className="fa-solid fa-triangle-exclamation"></i>
              <span>{thumbnailError?.message}</span>
            </div>
          </Reveal>
        </div>
        <div className={cx(styles.field, styles.name)}>
          <TextareaAutosize
            className={styles.input}
            ref={nameInputRef}
            placeholder="Enter Podcast name..."
            rows={1}
            maxRows={10}
            value={name}
            data-testid="podcast-name"
            onChange={handleNameChange}
            onPointerEnterCapture={() => { }}
            onPointerLeaveCapture={() => { }}
          />

          <Reveal show={nameError != null}>
            <div className={styles.error}>
              <i className="fa-solid fa-triangle-exclamation"></i>
              <span>{nameError?.message}</span>
            </div>
          </Reveal>
        </div>

        {editThumbnail && (
          <EditCourseThumbnail
            show={editThumbnail}
            type='podcast'
            banner={podcast?.banner || null}
            defaultBanner="/images/assets/podcastThumbnail.jpg"
            onFinish={handleThumbnailChange}
            onClose={() => setEditThumbnail(false)}
          />
        )}
      </div>
      <div className={styles.metadataRow}>
        <div className={cx(styles.field, styles.configurations)}>
          {podcastDuration && (
            <div className={styles.item}>
              <i className={cx(styles.duration, "paperflite-clock")} />
              <span>{podcastDuration}</span>
            </div>
          )}
          {podcastType && (
            <div className={styles.item}>
              <i className={cx(styles.speakers, "paperflite-speedomenter")} />
              <span>Type: {podcastType}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface PodcastDetailContainerProps {
  podcast: Podcast;
  // eslint-disable-next-line no-unused-vars
  onChange?: (property: string, value: any) => void;
}

const PodcastDetailContainer: React.FC<PodcastDetailContainerProps> = ({ podcast, onChange }) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className={styles.editDetailsContainer}>
      <TabBar
        className={cx(styles.tabs)}
        gap={32}
        tabSize="equal"
        active={activeTab}
        onSelectionChange={(tab) => setActiveTab(tab)}
      >
        <div className={cx(styles.tab, { [styles.active]: activeTab == 0 })}>
          Details
        </div>
        <div className={cx(styles.tab, { [styles.active]: activeTab == 1 })}>
          Transcript
        </div>
      </TabBar>
      <div className={styles.body}>
        <TabSection selected={activeTab} index={0}>
          <TopicsContainer podcast={podcast} onChange={onChange} />
        </TabSection>
        <TabSection selected={activeTab} index={1}>
          <div className={styles.transcripts}>
            <TranscriptsContainer podcast={podcast} />
          </div>
        </TabSection>
      </div>
    </div>
  );
}

interface RightPanelProps {
  podcast: Podcast;
}

const RightPanel: React.FC<RightPanelProps> = ({ podcast }) => {
  const dispatch = useDispatch();
  const podcastContext = useContext(PodcastContext);

  const publishedBy = _.get(podcast, "publishedBy");

  const published = useSelector((state: any) =>
    _.get(state, 'coaching.podcasts.publish.published') as boolean
  );  

  const completedLearners = useMemo(() => {
    return _.get(podcast, "completedLearners", 0);
  }, [podcast]);

  const totalLearners = useMemo(() => {
    return _.get(podcast, "totalLearners", 0);
  }, [podcast]);

  const averageTimeSpent = useMemo(() => {
    return _.get(podcast, "averageTimeSpent", 0);
  }, [podcast]);
  
  const totalTimeSpent = useMemo(() => {
    return _.get(podcast, "totalTimeSpent", 0);
  }, [podcast]);

  const [showSkillSlider, setShowSkillSlider] = useState(false);
  const [showAddLearner, setShowAddLearner] = useState(false);

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(
      updatePodcastProperty({
        podcast: podcast.id!,
        property,
        value,
      }),
    );
  };

  const onSkillClicked = () => {
    setShowSkillSlider(true);
  };

  const handleSkillRemove = (skill) => {
    const items = podcast?.skills?.filter((item: any) => item.id !== skill.id);
    handlePropertyChange("skills", items);
  };

  const handleAddSkillsFinish = (skills) => {
    handlePropertyChange("skills", skills);
  };

  const completionInfo = useMemo(() => {
    const rate = totalLearners > 0 ? Math.round((completedLearners / totalLearners) * 100) : 0;
    const text = `${completedLearners} of ${totalLearners} learners have completed`;
    return { rate, text };
  }, [completedLearners, totalLearners]);

  useEffect(() => {
    if (podcastContext.validationError) {
      const property = podcastContext.validationError.property;

      switch (property) {
        case "skills": {
          setShowSkillSlider(true);
          break;
        }

        default: {
          break;
        }
      }
    }

    podcastContext.gotoError?.(undefined);
  }, [podcastContext.validationError, setShowSkillSlider]);

  return (
    <div className={styles.right}>
      <animated.div className={styles.inner}>
        <div className={styles.detailsPane}>
          {(published || publishedBy) && (
          <ScrollableContainer
            distance={300}
            leftArrowComponent={LeftArrow}
            rightArrowComponent={RightArrow}
          >
            <div className={styles.metrics}>
              <Metrics
                name="completion rate"
                value={completionInfo.rate + "%"}
                subTitle={completionInfo.text}
              />
              
              <Metrics
                name="total listen time"
                value={FileService.formatDuration(totalTimeSpent, "short")}
              />

              <Metrics
                name="avg listen time"
                value={FileService.formatDuration(averageTimeSpent, "short")}
              />
            </div>
          </ScrollableContainer>
          )}
          <div className={styles.skillsContainer}>
            <p className={styles.heading}>
              Skills, learner will get ({podcast?.skills?.length || 0})
            </p>
            <div className={styles.skills}>
              <div
                className={styles.addSkillButton}
                onClick={() => onSkillClicked()}
              >
                <i className={cx(styles.icon, "paperflite-add")}></i>
              </div>

              {(podcast?.skills || []).map((skill: any) => {
                return (
                  <div key={skill.id} className={styles.skill}>
                    <div className={styles.name}>{skill.name}</div>
                    <i
                      className={cx("paperflite-close", styles.closeIcon)}
                      onClick={() => handleSkillRemove(skill)}
                    ></i>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.courseLearnersContainer}>
            <CourseLearners id={podcast.id!} course={podcast} />
          </div>
        </div>
      </animated.div>

      {showSkillSlider && (
        <SelectSkillsDialog
          show={showSkillSlider}
          selected={podcast?.skills || []}
          onFinish={handleAddSkillsFinish}
          onClose={() => setShowSkillSlider(false)}
        />
      )}

      <AddLearnersDialog
        show={showAddLearner}
        course={podcast}
        onFinish={() => setShowAddLearner(false)}
        onClose={() => setShowAddLearner(false)}
      />
    </div>
  );
}

interface TabSectionProps {
  selected: number;
  index: number;
  children: React.ReactNode;
}

const TabSection: React.FC<TabSectionProps> = ({
  index,
  selected,
  children,
}) => {
  const active = index === selected;
  const [animation] = useSpring(
    {
      height: active ? "auto" : 0,
      opacity: active ? 1 : 0,
      transform: active
        ? `translate3d(0,0,0)`
        : `translate3d(0, 100px, 0)`,
    },
    [active],
  );

  return (
    <animated.div
      style={animation}
      className={cx(styles.tabBody, styles.stack)}
    >
      {active && children}
    </animated.div>
  );
};

interface TopicsContainerProps {
  podcast: Podcast;
  // eslint-disable-next-line no-unused-vars
  onChange?: (property: string, value: any) => void;
}

const TopicsContainer: React.FC<TopicsContainerProps> = ({ podcast, onChange }) => {
  const description = podcast?.description || "";
  const topics = podcast?.topics || [];
  const hasTopics = _.size(topics) > 0;

  const descriptionError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, { type: "podcast", property: "description" });
  });

  const emptyTransition = useTransition(!hasTopics, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  const topicsTransition = useTransition(hasTopics, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });


  const handleDescriptionChange = (e: any) => {
    const content = e.target.value;
    onChange?.("description", content);
  };

  return (
    <div className={styles.topicsContainer}>
      {emptyTransition((style, item) => item && (
        <animated.div className={styles.stack} style={style}>
          <div className={styles.topics}>
            <div className={styles.topicsContent}>
              {_.times(5, (index) => (
                <div key={index} className={cx(styles.topic, styles.empty)}>
                  <span className={styles.index}></span>
                  <span className={styles.separator}>/</span>
                  <span className={styles.name}></span>
                </div>
              ))}
            </div>
          </div>
        </animated.div>
      ))}

      {topicsTransition((style, item) => item && (
        <animated.div className={styles.stack} style={style}>
          <div className={cx(styles.field, styles.description)}>
            <TextareaAutosize
              className={styles.descriptionInput}
              rows={1}
              value={description}
              placeholder={
                "Enter your podcast description here..."
              }
              spellCheck={true}
              onChange={handleDescriptionChange}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />

            <Reveal show={descriptionError != null}>
              <div className={styles.error}>
                <i className="fa-solid fa-triangle-exclamation"></i>
                <span>{descriptionError?.message}</span>
              </div>
            </Reveal>
          </div>
          <div className={styles.topics}>
            <div className={styles.title}>here’s the list of topics, we have covered in this podcast</div>
            <div className={styles.topicsContent}>
              {topics.map((topic: any, index: number) => (
                <div key={index} className={styles.topic}>
                  <span className={styles.index}>{String(index + 1).padStart(2, '0')}</span>
                  <span className={styles.separator}>/</span>
                  <span className={styles.name}>{topic.name}</span>
                </div>
              ))}
            </div>
          </div>
        </animated.div>
      ))}
    </div>
  );
}

interface MetricsProps {
  name: string;
  value?: any;
  subTitle?: string;
}

const Metrics: React.FC<MetricsProps> = ({name, value, subTitle}) => {
  return (
    <div className={styles.infoCard}>
      <div className={styles.metrics}>
        <span className={styles.name}>{name}</span>
        <span className={styles.value}>{value}</span>
        <span className={styles.subTitle}>{subTitle}</span>
      </div>
    </div>
  );
};

const LeftArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.leftArrow)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

const RightArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.rightArrow)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

interface TranscriptsContainerProps {
  podcast: Podcast;
}

const TranscriptsContainer: React.FC<TranscriptsContainerProps> = ({ podcast }) => {
  const { loading, transcripts } = useSelector((state: any) => {
    const loading = _.get(state.coaching.podcasts, `edit.item.transcription.loading`, false);
    const transcripts = _.get(state.coaching.podcasts, `edit.item.transcription.item`) || [];

    return {
      loading: loading,
      transcripts: transcripts,
    };
  });

  const emptyTranscripts = _.size(transcripts) === 0 && !loading;

  const emptyTransition = useTransition(emptyTranscripts, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  const transcriptsTransition = useTransition(!emptyTranscripts, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  return (
    <div className={styles.transcriptsContainer}>
      {emptyTransition((style, item) => item && (
        <animated.div className={styles.stack} style={style}>
          <div className={styles.emptyTranscripts}>
            {_.times(5, (index) => (
              <PodcastTranscriptRowPlaceholder key={index} />
            ))}
          </div>
        </animated.div>
      ))}

      {transcriptsTransition((style, item) => item && (
        <animated.div className={styles.stack} style={style}>
          <PodcastTranscriptsContainer loading={loading} transcripts={transcripts} />
        </animated.div>
      ))}
    </div>
  );
}